import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { api } from '@/services/api'

export type Theme = 'light' | 'dark' | 'system'

export const useThemeStore = defineStore('theme', () => {
  // State
  const theme = ref<Theme>('system')
  const isLoading = ref(false)

  // Computed
  const isDark = computed(() => {
    if (theme.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return theme.value === 'dark'
  })

  const effectiveTheme = computed(() => {
    return isDark.value ? 'dark' : 'light'
  })

  // System theme detection
  const systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)')
  
  // Watch for system theme changes when using system preference
  systemThemeQuery.addEventListener('change', () => {
    if (theme.value === 'system') {
      applyTheme()
    }
  })

  // Actions
  const setTheme = async (newTheme: Theme, saveToServer: boolean = true) => {
    const oldTheme = theme.value
    theme.value = newTheme
    
    // Apply theme immediately
    applyTheme()
    
    // Save to localStorage
    localStorage.setItem('theme', newTheme)
    
    // Save to server if authenticated and requested
    if (saveToServer) {
      try {
        isLoading.value = true
        await api.patch('/v1/auth/profile', { theme_preference: newTheme })
      } catch (error) {
        console.error('Failed to save theme preference:', error)
        // Revert on error
        theme.value = oldTheme
        applyTheme()
        throw error
      } finally {
        isLoading.value = false
      }
    }
  }

  const applyTheme = () => {
    const html = document.documentElement
    
    if (isDark.value) {
      html.classList.add('dark')
      html.classList.remove('light')
    } else {
      html.classList.add('light')
      html.classList.remove('dark')
    }
    
    // Update meta theme-color for mobile browsers
    updateMetaThemeColor()
  }

  const updateMetaThemeColor = () => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark.value ? '#1f2937' : '#ffffff')
    }
  }

  const initializeTheme = (userTheme?: Theme) => {
    // Priority: userTheme > localStorage > system
    let initialTheme: Theme = 'system'
    
    if (userTheme) {
      initialTheme = userTheme
    } else {
      const savedTheme = localStorage.getItem('theme') as Theme
      if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
        initialTheme = savedTheme
      }
    }
    
    theme.value = initialTheme
    applyTheme()
  }

  const cycleTheme = async () => {
    const themes: Theme[] = ['light', 'dark', 'system']
    const currentIndex = themes.indexOf(theme.value)
    const nextTheme = themes[(currentIndex + 1) % themes.length]
    await setTheme(nextTheme)
  }

  const getThemeIcon = () => {
    switch (theme.value) {
      case 'light':
        return 'sun'
      case 'dark':
        return 'moon'
      case 'system':
        return 'monitor'
      default:
        return 'monitor'
    }
  }

  const getThemeLabel = () => {
    switch (theme.value) {
      case 'light':
        return 'Light Mode'
      case 'dark':
        return 'Dark Mode'
      case 'system':
        return 'System'
      default:
        return 'System'
    }
  }

  // Initialize theme on store creation
  const init = () => {
    // Load from localStorage initially
    const savedTheme = localStorage.getItem('theme') as Theme
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      theme.value = savedTheme
    }
    applyTheme()
  }

  // Initialize immediately
  init()

  return {
    // State
    theme,
    isLoading,
    
    // Computed
    isDark,
    effectiveTheme,
    
    // Actions
    setTheme,
    applyTheme,
    initializeTheme,
    cycleTheme,
    getThemeIcon,
    getThemeLabel
  }
})
